import { useEffect, useState } from 'react';
import type { Session } from '../App';
import { Container, PageHeader } from '../App';

async function authedFetch(path: string, init?: RequestInit) {
  const token = localStorage.getItem('forge.token');
  const headers = new Headers(init?.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const res = await fetch(path, { ...init, headers });
  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  return res;
}

interface Props { session: Session; }

interface ListingView {
  id: string;
  display_name: string;
  available: boolean;
  remaining_today: number | null;
  cost: { entries: { ref_key: string; amount: { value: number } }[] };
}

export default function Shop({ session }: Props) {
  const playerId = session.playerId;
  const [stores, setStores] = useState<string[]>([]);
  const [selected, setSelected] = useState('');
  const [listings, setListings] = useState<ListingView[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    authedFetch(`/api/v1/games/${session.gameId}/modules/shop/stores`)
      .then(r => r.json())
      .then((d: { stores?: string[] }) => {
        const list = Array.isArray(d?.stores) ? d.stores : [];
        setStores(list);
        if (list.length > 0 && !selected) setSelected(list[0]);
      })
      .catch(e => {
        setStores([]);
        setError((e as Error).message);
      });
  }, [session.gameId]);

  const loadListings = () => {
    if (!selected) {
      setListings([]);
      return;
    }
    authedFetch(`/api/v1/games/${session.gameId}/modules/shop/stores/${selected}/listings/player/${playerId}`)
      .then(r => r.json())
      .then((d: ListingView[] | { listings?: ListingView[] } | null) => {
        const list = Array.isArray(d)
          ? d
          : Array.isArray((d as { listings?: ListingView[] } | null)?.listings)
          ? (d as { listings: ListingView[] }).listings
          : [];
        setListings(list);
      })
      .catch(e => {
        setListings([]);
        setError((e as Error).message);
      });
  };
  useEffect(loadListings, [session.gameId, selected, playerId]);

  const buy = async (listingId: string) => {
    setBusy(true); setError(null);
    try {
      await authedFetch(
        `/api/v1/games/${session.gameId}/modules/shop/purchase/${listingId}/player/${playerId}`,
        { method: 'POST' },
      );
      loadListings();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <Container>
      <PageHeader title="Shop" subtitle="Browse stores and test purchases for the selected player." />
      {error && <div className="error-banner">{error}</div>}

      <div className="card mb-5">
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
          <div>
            <label className="block text-[11.5px] uppercase tracking-wider font-medium text-fg-dim mb-1.5">Store</label>
            <select className="input" value={selected} onChange={e => setSelected(e.target.value)}>
              {stores.length === 0 && <option value="">No stores published</option>}
              {stores.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <button className="btn" onClick={loadListings}>Refresh</button>
        </div>
      </div>

      {listings.length === 0 ? (
        <div className="empty">
          <div className="text-fg-2 font-medium mb-1">No listings</div>
          <div className="text-xs text-fg-muted">Create stores in Config Explorer → Shop, publish, then view here.</div>
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Listing</th><th>Cost</th><th>Remaining</th><th></th></tr></thead>
            <tbody>
              {listings.map(l => (
                <tr key={l.id}>
                  <td className="font-medium">{l.display_name}</td>
                  <td className="text-sm">{l.cost.entries.map(e => `${e.ref_key}: ${e.amount.value}`).join(', ')}</td>
                  <td>{l.remaining_today != null ? l.remaining_today : '∞'}</td>
                  <td>
                    <button className="btn btn-primary" disabled={busy || !l.available} onClick={() => buy(l.id)}>
                      {l.available ? 'Buy' : 'Sold out'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Container>
  );
}
